/**
 * Plan 13-02 — ApproveDialog
 *
 * Dialog for approving a single pending entry.
 * Remarks are optional (max 500 chars). Uses react-hook-form + Zod via useZodForm.
 */

import type { JSX } from 'react';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useApproveMutation } from '@/hooks/useLogsheetCheckerQueries';
import { useZodForm } from '@/lib/forms/useZodForm';
import { toast } from '@/lib/toast';

const approveSchema = z.object({
  remarks: z.string().max(500).optional(),
});

export interface ApproveDialogProps {
  readonly open: boolean;
  readonly entryId: number;
  readonly onClose: () => void;
  readonly onSuccess: () => void;
}

export function ApproveDialog({
  open,
  entryId,
  onClose,
  onSuccess,
}: ApproveDialogProps): JSX.Element {
  const form = useZodForm({
    schema: approveSchema,
    defaultValues: { remarks: '' },
  });

  const mutation = useApproveMutation();

  function handleClose(): void {
    form.reset();
    mutation.reset();
    onClose();
  }

  const onSubmit = form.handleSubmit((values) => {
    const remarks = values.remarks?.trim();
    const body = remarks !== undefined && remarks !== '' ? { remarks } : {};
    mutation.mutate(
      { id: entryId, body },
      {
        onSuccess: () => {
          toast.success('Entry approved successfully');
          form.reset();
          onSuccess();
        },
        onError: (err) => {
          toast.error('Could not approve entry', {
            description: err.detail ?? err.message,
          });
        },
      },
    );
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) handleClose();
      }}
    >
      <DialogContent data-testid="approve-dialog">
        <DialogHeader>
          <DialogTitle>Approve Entry #{entryId}</DialogTitle>
          <DialogDescription>
            Approve this logsheet entry. It will move to Checked status and be forwarded to the
            Approver. Add optional remarks for context.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} noValidate>
          <div className="space-y-3 py-2">
            <div className="space-y-1">
              <Label htmlFor="approve-remarks">Remarks (optional)</Label>
              <Textarea
                id="approve-remarks"
                placeholder="Optional remarks for the approver…"
                rows={3}
                data-testid="approve-dialog-remarks"
                {...form.register('remarks')}
              />
              {form.formState.errors.remarks !== undefined && (
                <p className="text-xs text-destructive">{form.formState.errors.remarks.message}</p>
              )}
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={mutation.isPending}
              data-testid="approve-dialog-cancel"
            >
              Cancel
            </Button>
            <Button type="submit" disabled={mutation.isPending} data-testid="approve-dialog-submit">
              {mutation.isPending ? 'Approving…' : 'Approve'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
